import React, { useCallback, useEffect, useState } from 'react'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridColumnProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  WelcomeAccountDeviceActivityDto,
  WelcomeAccountDeviceActivityDtoKey,
} from 'api/Trading-Accounts/Types/WelcomeAccountDeviceActivityDto'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableWithExport } from 'utils/table/withExport'

import { WelcomeAccountsDetailTable } from './LeadWelcomeAccountsAnalyticsDetailTable'

interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const columns: GridColumn[] = [
  {
    title: 'Device Key',
    field: WelcomeAccountDeviceActivityDtoKey.deviceKey,
    className: 'table-columns__content',
    show: true,
  },
  {
    title: 'Number of Welcome Accounts',
    field: WelcomeAccountDeviceActivityDtoKey.count,
    className: 'table-columns__number',
    show: true,
  },
]

interface WelcomeTradingAccount {
  id: string
  name: string
}

interface LeadWelcomeAccountsAnalyticsDeviceKeyProps {
  activities: WelcomeAccountDeviceActivityDto[]
  paginationState: Page
  onExpandChange(event: GridExpandChangeEvent): void
  onGridPageChange(event: GridPageChangeEvent): void
}

export const LeadWelcomeAccountsAnalyticsDeviceKeyUI: React.FC<
  LeadWelcomeAccountsAnalyticsDeviceKeyProps
> = (props) => {
  const { activities, paginationState, onExpandChange, onGridPageChange } = props

  return (
    <div style={{ margin: 16 }}>
      <TableWithExport data={activities} columns={columns}>
        <Grid
          resizable
          data={activities.slice(paginationState.skip, paginationState.take + paginationState.skip)}
          skip={paginationState.skip}
          take={paginationState.take}
          total={activities.length}
          detail={WelcomeAccountsDetailTable}
          onPageChange={onGridPageChange}
          onExpandChange={onExpandChange}
          pageable={pageableSettings}
          expandField='expanded'
          style={{ zIndex: 0 }}
        >
          {columns
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </TableWithExport>
    </div>
  )
}

interface OuterProps {
  data: WelcomeTradingAccount
}

export const LeadWelcomeAccountsAnalyticsDeviceKey: React.FC<OuterProps> = (props) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [activities, setActivities] = useState<WelcomeAccountDeviceActivityDto[]>([])
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const callback = useCallback(() => {
    return tradingAccountsApiClient.getWelcomeTradingAccountDeviceActivity(props.data.id)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data } = useFetchOne(callback)

  useEffect(() => {
    if (data) {
      setActivities(data)
    }
  }, [data])

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    setActivities(
      activities.map((x) =>
        x.deviceKey === event.dataItem.deviceKey
          ? {
              ...x,
              expanded: event.value,
            }
          : x
      )
    )
  }

  const handleGridPageChange = (event: GridPageChangeEvent) => {
    setPaginationState({
      skip: event.page.skip,
      take: event.page.take,
    })
  }

  return (
    <LeadWelcomeAccountsAnalyticsDeviceKeyUI
      activities={activities}
      paginationState={paginationState}
      onExpandChange={handleExpandChange}
      onGridPageChange={handleGridPageChange}
    />
  )
}
